import * as Sqrl from 'squirrelly';

import * as a11y from '../vendor/theme-scripts/theme-a11y';
import FetchError from '../util/fetch-error';
import {fetchProduct} from '../util/fetch-product';
import {formatPrices} from '../util/format-prices';
import debounce from '../util/debounce';

const selectors = {
  input: 'data-search-input',
  productTemplate: '[product-grid-item-template]',
  pageTemplate: '[page-item-template]',
  noresultTemplate: '[noresult-item-template]',
  productsWrapper: '[data-products-wrap]',
  articlesWrapper: '[data-articles-wrap]',
  pagesWrapper: '[data-pages-wrap]',
  productWrapperOuter: '[data-product-wrap-outer]',
  viewAll: '[data-view-all]',
  productImage: '[data-product-image]',
  searchPopdown: 'nav-search',
};

const classes = {
  loadingClass: 'is-searching',
  resultsVisibleClass: 'results--visible',
  hiddenClass: 'is-hidden',
  focusEnabled: 'is-focused',
};

class SearchPredictive {
  constructor(input) {
    this.input = input;
    this.key = this.input.getAttribute(selectors.input);
    const appendSelector = `[id="${this.key}"]`;
    this.append = document.querySelector(appendSelector);
    this.productTemplate = document.querySelector(selectors.productTemplate).innerHTML;
    this.pageTemplate = document.querySelector(selectors.pageTemplate).innerHTML;
    this.noresultTemplate = document.querySelector(selectors.noresultTemplate).innerHTML;
    this.productsWrapper = this.append.querySelector(selectors.productsWrapper);
    this.pagesWrapper = this.append.querySelector(selectors.pagesWrapper);
    this.articlesWrapper = this.append.querySelector(selectors.articlesWrapper);
    this.focusedContainer = this.productsWrapper;
    this.viewAll = this.append.querySelector(selectors.viewAll);
    this.popdown = document.getElementById(selectors.searchPopdown);
    this.productWrapperOuter = this.popdown.querySelector(selectors.productWrapperOuter);
    this.result = null;
    this.accessibility = a11y;
    this.initSearch();
  }

  initSearch() {
    this.input.addEventListener(
      'input',
      debounce(
        function (event) {
          const val = event.target.value;
          if (val && val.length > 1) {
            this.productWrapperOuter.classList.add(classes.loadingClass);
            this.render(val);
          } else {
            this.reset();
          }
        }.bind(this),
        300
      )
    );
    this.input.addEventListener('clear', this.reset.bind(this));
  }

  render(terms) {
    const searchType = theme.settings.searchType;
    fetch(`/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=${searchType}&resources[limit]=10&resources[options][unavailable_products]=last`)
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.result = response.resources.results;

        return this.fetchProducts(this.result.products);
      })
      .then((response) => {
        this.popdown.classList.add(classes.resultsVisibleClass);

        this.reset(false);

        if (this.result.articles !== undefined) {
          if (this.result.articles.length === 0) {
            this.injectHTML(this.articlesWrapper, this.renderNoResult());
          } else {
            this.injectHTML(this.articlesWrapper, this.fetchPages(this.result.articles));
            this.focusedContainer = this.articlesWrapper;
          }
        }

        if (this.result.pages !== undefined) {
          if (this.result.pages.length === 0) {
            this.injectHTML(this.pagesWrapper, this.renderNoResult());
          } else {
            this.injectHTML(this.pagesWrapper, this.fetchPages(this.result.pages));
            this.focusedContainer = this.pagesWrapper;
          }
        }

        if (response === '') {
          this.viewAll.classList.add(classes.hiddenClass);
          this.injectHTML(this.productsWrapper, this.renderNoResult());
        } else {
          this.viewAll.classList.remove(classes.hiddenClass);
          this.viewAll.setAttribute('href', `${theme.routes.search_url}?type=product&q=${terms}&options[prefix]=last`);
          this.injectHTML(this.productsWrapper, response);
        }

        this.productWrapperOuter.classList.remove(classes.loadingClass);

        const isFocusEnabled = document.body.classList.contains(classes.focusEnabled);

        if (isFocusEnabled) {
          this.accessibility.trapFocus(this.popdown, {
            elementToFocus: this.focusedContainer,
          });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  reset(clearTerms = true) {
    this.productsWrapper.innerHTML = '';

    if (this.pagesWrapper !== null) {
      this.pagesWrapper.innerHTML = '';
    }

    if (this.articlesWrapper !== null) {
      this.articlesWrapper.innerHTML = '';
    }

    this.input.val = '';
    this.accessibility.removeTrapFocus();

    if (clearTerms) {
      this.popdown.classList.remove(classes.resultsVisibleClass);
    }
  }

  injectHTML(target, pageHTML) {
    target.innerHTML += pageHTML;
  }

  fetchProducts(products) {
    const promises = [];
    // Limit products to 4
    products.slice(0, 4).forEach((product) => {
      // because of a translation bug in the predictive search API
      // we need to fetch the product JSON from the handle
      promises.push(
        fetchProduct(product.handle).then((productJSON) => {
          const formatted = formatPrices(productJSON);
          return this.renderProduct(formatted);
        })
      );
    });

    return Promise.all(promises).then((result) => {
      let str = '';
      result.forEach((render) => {
        str += render;
      });
      return str;
    });
  }

  fetchPages(pages) {
    let html = '';
    pages.forEach((page) => {
      html += this.renderPage(page);
    });

    return html;
  }

  renderProduct(product) {
    const stripHtmlRegex = /(<([^>]+)>)/gi;
    const title = product.title.replace(stripHtmlRegex, '');
    let media = null;
    let image = '';
    let blankImage = theme.assets.blank;

    if (product.media !== undefined) {
      media = product.media[0];
    }

    if (media) {
      image = {
        thumb: media.preview_image.src,
        alt: media.preview_image.alt,
      };
    } else {
      image = {
        thumb: theme.assets.no_image,
        alt: '',
      };
    }

    const updateValues = {
      ...product,
      title,
      image,
    };

    return Sqrl.render(this.productTemplate, {product: updateValues});
  }

  renderPage(page) {
    const stripHtmlRegex = /(<([^>]+)>)/gi;
    const title = page.title.replace(stripHtmlRegex, '');

    const updateValues = {
      ...page,
      title,
    };

    return Sqrl.render(this.pageTemplate, {page: updateValues});
  }

  renderNoResult() {
    const text = theme.translations.no_results;

    const updateValues = {
      text,
    };

    return Sqrl.render(this.noresultTemplate, {item: updateValues});
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }
}

export {SearchPredictive};
